import * as moment from 'moment';
import * as _ from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { DonorConnectTicketsWrapper } from './styles';
import { CellFocusedEvent, ColDef, ColSpanParams, ColumnMovedEvent, ColumnResizedEvent, DragStoppedEvent, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClickedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IGroup, IListItem, ITicket, ITicketForList, IUser } from '@nkr/models/index';
import { FormControl } from 'react-bootstrap';
import { DisplayType, TicketEntityType, TicketStatus, TicketType, UserGroup } from '@nkr/enums/index';
import { useMemo } from 'react';

const disableRowSelectionColumns = ['Agent', 'Status'];

interface IAgentRendererProps extends ICellRendererParams { groupList: IGroup[], onChangeAssignedUser: (ticketId: number, userId: number) => void };
interface IStatusRendererProps extends ICellRendererParams { statusList: IListItem[], onChangeStatus: (ticketId: number, status: number) => void };

interface IProps {
    data: ITicketForList[],
    display: DisplayType,
    userList: IUser[],
    userGroupList: IGroup[],
    statusList: IListItem[],
    onGridReady: (e: GridReadyEvent) => void,
    onRowClicked: (e: RowClickedEvent) => void,
    onColumnResized: (e: ColumnResizedEvent) => void,
    onColumnMoved: (e: ColumnMovedEvent) => void,
    onDragStopped: (e: DragStoppedEvent) => void,
    onMouseOver: (e: React.MouseEvent, currentTicket: number) => void,
    onMouseOut: () => void,
    onChangeAssignedUser: (ticketId: number, userId: number) => void,
    onChangeStatus: (ticketId: number, status: number) => void,
}

const DonorConnectGrid = (props: IProps) => {
    const getRowStyle = (params: any) => {
        let data = params.data as ITicket;

        if (data.ticketStatusId == TicketStatus.Closed || data.ticketStatusId == TicketStatus.Resolved) {
            return { color: '#a1a1a1' };
        }

        if (data.entityType == TicketEntityType.Center || data.ticketTypeId == TicketType.ContactPageNonMemberCenter || data.ticketTypeId == TicketType.MemberCenterRequest || data.isCenter == true) {
            return { backgroundColor: 'yellow' };
        }
    };

    const cols: ColDef[] = [
        {
            colId: 'TicketNumber',
            field: 'id',
            headerName: 'Ticket #',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Ticket #</SortableHeaderComponent>,
            width: 85,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
        },

        {
            colId: 'FromSubject',
            headerName: 'From and Subject',
            headerClass: 'list-header',
            headerComponentFramework: () => <span style={{ cursor: 'pointer' }}>From &amp; Subject</span>,
            width: 400,
            cellClass: 'list-cell-left',
            sortable: false,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITicketForList;
                let { contactId, contactName, contactEmail, isMissingData } = data;

                if (contactId > 0 && isMissingData == false)
                    return (
                        <div style={{ textAlign: 'left', width: '100%' }} onMouseOver={(e: React.MouseEvent) => props.onMouseOver(e, params.data.id)} onMouseOut={props.onMouseOut}>
                            {contactEmail ? `${contactName} (${contactEmail})` : contactName}<br />
                            subject: {data.subject ? data.subject : '[No Subject]'}
                        </div>
                    );

                if (isMissingData)
                    return (
                        <div className='bg-danger' style={{ padding: 5, width: '100%' }}>
                            There is an issue with this ticket, please alert the developers...
                        </div>
                    );

                return null;
            },
            colSpan: (params: ColSpanParams) => {
                const data = params.data as ITicketForList;

                if (data.isMissingData) {
                    return 8;
                }

                return 1;
            }
        },

        {
            colId: 'Agent',
            field: 'assignedUserId',
            headerName: 'Agent',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Agent</SortableHeaderComponent>,
            width: 275,
            cellClass: 'list-cell-dropdown',
            sortable: true,
            resizable: true,
            cellRendererParams: { groupList: props.userGroupList, onChangeAssignedUser: props.onChangeAssignedUser },
            cellRendererFramework: (params: IAgentRendererProps) =>
                <>
                    <FormControl
                        componentClass='select'
                        bsSize='sm'
                        style={{ color: params.data.ticketStatusId == TicketStatus.Closed || params.data.ticketStatusId == TicketStatus.Resolved ? '#bbb' : '#555', padding: '3px 6px', fontSize: 12 }}
                        value={params.data.assignedUserId}
                        onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onChangeAssignedUser(params.data.id, parseInt(e.currentTarget.value))}
                    >
                        <option value={0}>- PENDING ASSIGNMENT -</option>
                        {params.groupList.map(group => {
                            let userList = props.userList.filter(x => x.groupId == group.id);
                            let sortedUserList = _.sortBy(userList, [item => item.firstName]);

                            return (
                                <optgroup key={'group-' + group.id} label={group.id == UserGroup.Admin ? 'NKR' : group.name}>
                                    {sortedUserList.map((item) =>
                                        <option key={`assigned-user-${item.id}`} value={item.id}>{item.firstName} {item.lastName}</option>
                                    )}
                                </optgroup>
                            );
                        })}
                    </FormControl>
                </>
        },

        {
            colId: 'RequestType',
            field: 'typeDescription',
            headerName: 'Request Type',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Request Type</SortableHeaderComponent>,
            width: 330,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : <span style={{ fontStyle: 'italic' }}>[Not Specified]</span>}
                </>
        },

        {
            colId: 'Center',
            field: 'centerAlias',
            headerName: 'Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 185,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
        },

        {
            colId: 'Status',
            field: 'ticketStatus',
            headerName: 'Status',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            width: 150,
            cellClass: 'list-cell-dropdown',
            sortable: true,
            resizable: true,
            cellRendererParams: { statusList: props.statusList, onChangeStatus: props.onChangeStatus },
            cellRendererFramework: (params: IStatusRendererProps) => {
                return (
                    <>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            style={{ color: params.data.ticketStatusId == TicketStatus.Closed || params.data.ticketStatusId == TicketStatus.Resolved ? '#bbb' : '#555', padding: '3px 6px', fontSize: 12 }}
                            value={params.value}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onChangeStatus(params.data.id, parseInt(e.currentTarget.value))}
                        >
                            {params.statusList.map((item) =>
                                <option key={`status-${item.id}`} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </>
                );
            }
        },

        {
            colId: 'Tier',
            field: 'tierDescription',
            headerName: 'Tier',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Tier</SortableHeaderComponent>,
            width: 80,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    {params.value ? params.value : <span style={{ fontStyle: 'italic' }}>[Not Specified]</span>}
                </>
        },

        {
            colId: 'DateEntered',
            field: 'createdDateTime',
            headerName: 'Date Entered',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Entered</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.data.createdDateTime).format('MM/DD/YYYY HH:mm')}</>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ITicketForList;
                return new Date(data.createdDateTime);
            }
        },

        {
            colId: 'lastUpdatedDateTime',
            field: 'lastUpdatedDateTime',
            headerName: 'Submitted',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Updated</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.data.lastUpdatedDateTime).format('MM/DD/YYYY HH:mm')}</>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITicketForList;
                return new Date(data.lastUpdatedDateTime);
            }
        },

        {
            colId: 'closedDateTime',
            field: 'closedDateTime',
            headerName: 'Date Closed',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Closed</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value != null ? moment(params.value).format('MM/DD/YYYY HH:mm') : '--'}</>,
            valueGetter: (props: ValueGetterParams) => {
                let data = props.data as ITicketForList;

                if (data.closedDateTime != null) {
                    return new Date(data.closedDateTime);
                }

                return null;
            }
        },

        {
            colId: 'Urgent',
            field: 'urgencyId',
            headerName: 'Urgent',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Urgent</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell',
            sortable: true,
            resizable: true,
            cellRenderer: (params: any) => parseInt(params.value) != 0 ? 'Yes' : 'No'
        }
    ];

    const columnDefs = useMemo(() => cols, [props.statusList, props.userList]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <DonorConnectTicketsWrapper width='100%' fontSize='13px' height={props.display != DisplayType.NoMenuNoTabs ? '65vh' : '84vh'} className='ag-theme-alpine-dark' >
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                suppressCellSelection={true}
                headerHeight={35}
                rowHeight={42}
                getRowStyle={getRowStyle}
                suppressNoRowsOverlay={false}
                noRowsOverlayComponentFramework={()=><>No tickets match the search selections...</>}
                onGridReady={props.onGridReady}
                onRowClicked={props.onRowClicked}
                onColumnResized={props.onColumnResized}
                onColumnMoved={props.onColumnMoved}
                onDragStopped={props.onDragStopped}
                onCellFocused={(e: CellFocusedEvent) => {
                    if (e.column && disableRowSelectionColumns.indexOf(e.column.getColDef().colId) >= 0) {
                        (e.api as any).gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
                    } else {
                        (e.api as any).gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
                    }
                }}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => {
                    e.api.sizeColumnsToFit();
                }}
            />
        </DonorConnectTicketsWrapper >
    );
}

export default DonorConnectGrid;